import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Full-screen pose preview, styled to the Figma "Picture Day Preview": a large
// watermarked image with a pose counter + favorite heart, a thumbnail strip with
// favorite indicators, and a bottom action bar ("Select all as favorites" +
// "Shop this photo"). Arrow keys navigate; Esc closes.
const Lightbox = ({
  poses, index, favorites, onIndex, onToggleFavorite, onShop, onSelectAll, onClose, backgroundUrl,
}) => {
  const pose = poses[index];
  // Show a spinner over the big image until the current pose's image loads
  // (the previews aren't pre-fetched on the season-cards landing).
  const [loadedId, setLoadedId] = useState(null);

  // Don't let a backdrop click dismiss the lightbox while it's still opening /
  // loading its first image: on a cold load the click that opened it (or an
  // impatient second click) can otherwise land on the freshly-mounted backdrop
  // and immediately close it. Becomes closable once the first image loads, or
  // after a safety timeout. The X button + Esc close anytime.
  const [closable, setClosable] = useState(false);
  // A generous safety net in case the image never fires load/error.
  useEffect(() => {
    const tid = window.setTimeout(() => setClosable(true), 4000);
    return () => window.clearTimeout(tid);
  }, []);
  // Once the first image has loaded, the backdrop can close normally.
  useEffect(() => { if (loadedId != null) setClosable(true); }, [loadedId]);
  const closeOnBackdrop = (e) => {
    if (closable && e.target === e.currentTarget) onClose();
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onIndex(Math.min(index + 1, poses.length - 1));
      if (e.key === 'ArrowLeft') onIndex(Math.max(index - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, poses.length, onIndex, onClose]);

  if (!pose) return null;
  const label = pose.number != null ? `Pose ${pose.number}` : 'Photo';
  const favorite = favorites.has(pose.id);
  const favCount = poses.filter((p) => favorites.has(p.id)).length;
  const imgStyle = backgroundUrl ? { backgroundImage: `url(${backgroundUrl})` } : undefined;

  return (
    <div className="sg-lb-backdrop" onClick={closeOnBackdrop} role="presentation">
      <div className="sg-lb-head">
        <h2>Your Photos</h2>
        <p>Mark your favorite poses with the heart icon</p>
      </div>
      <button type="button" className="sg-lb-close" onClick={onClose} aria-label="Close">×</button>

      <div className="sg-lb" onClick={(e) => e.stopPropagation()} role="presentation">
        <div className="sg-lb-stage">
          {index > 0 && (
            <button
              type="button"
              className="sg-lb-nav sg-lb-prev"
              onClick={() => onIndex(index - 1)}
              aria-label="Previous"
            >
              ‹
            </button>
          )}

          <div className="sg-lb-imgwrap">
            {loadedId !== pose.id && <span className="sg-lb-spinner" aria-label="Loading photo" />}
            <img
              src={pose.watermark_url}
              alt={label}
              className={`sg-lb-img${loadedId === pose.id ? ' is-loaded' : ''}`}
              style={imgStyle}
              onLoad={() => setLoadedId(pose.id)}
              onError={() => { setLoadedId(pose.id); setClosable(true); }}
            />
            <span className="sg-lb-counter">{`${index + 1} / ${poses.length}`}</span>
            {favorite && <span className="sg-lb-favbadge">♥ Favorite</span>}
            <button
              type="button"
              className={`sg-lb-heart${favorite ? ' is-on' : ''}`}
              onClick={() => onToggleFavorite(pose.id)}
              aria-pressed={favorite}
              aria-label={favorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              ♥
            </button>
          </div>

          {index < poses.length - 1 && (
            <button
              type="button"
              className="sg-lb-nav sg-lb-next"
              onClick={() => onIndex(index + 1)}
              aria-label="Next"
            >
              ›
            </button>
          )}
        </div>

        {poses.length > 1 && (
          <div className="sg-lb-thumbs">
            {poses.map((p, i) => (
              <button
                key={p.id}
                type="button"
                className={`sg-lb-thumb${i === index ? ' is-active' : ''}`}
                onClick={() => onIndex(i)}
                aria-label={`View pose ${i + 1}`}
              >
                <img src={p.watermark_url} alt="" style={imgStyle} loading="lazy" />
                {favorites.has(p.id) && <span className="sg-lb-thumb-fav">♥</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="sg-lb-actions" onClick={(e) => e.stopPropagation()} role="presentation">
        <button type="button" className="sg-lb-selectall" onClick={onSelectAll}>
          Select all as favorites
        </button>
        <button type="button" className="sg-lb-shop" onClick={() => onShop(pose)}>
          {favCount > 0 ? `Shop this photo · ${favCount} favorite${favCount === 1 ? '' : 's'}` : 'Shop this photo'}
        </button>
      </div>
    </div>
  );
};

Lightbox.propTypes = {
  poses: PropTypes.arrayOf(PropTypes.object).isRequired,
  index: PropTypes.number.isRequired,
  favorites: PropTypes.instanceOf(Set).isRequired,
  onIndex: PropTypes.func.isRequired,
  onToggleFavorite: PropTypes.func.isRequired,
  onShop: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  backgroundUrl: PropTypes.string,
};

Lightbox.defaultProps = {
  backgroundUrl: null,
};

export default Lightbox;
